import React from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Brightness4,
  Brightness7,
  Person,
  Settings,
  Logout,
  QrCodeScanner,
  Apple,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { toggleSidebar, setTheme, openDialog } from '../../store/slices/uiSlice';
import { signOutSuccess } from '../../store/slices/authSlice';
import { AuthService } from '../../firebase/authService';

const AppBar: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { theme } = useSelector((state: RootState) => state.ui);
  
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    try {
      await AuthService.signOut();
      dispatch(signOutSuccess());
      handleMenuClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleThemeToggle = () => {
    dispatch(setTheme(theme === 'light' ? 'dark' : 'light'));
  };

  const handleQRScanner = () => {
    dispatch(openDialog({ type: 'qr-scanner' }));
  };

  return (
    <MuiAppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: theme === 'dark' ? '#0a1628' : '#0f2744',
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="toggle drawer"
          onClick={() => dispatch(toggleSidebar())}
          edge="start"
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        
        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          My Inventory
        </Typography>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <IconButton onClick={handleQRScanner} title="Scan QR Code" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#64b5f6' } }}>
            <QrCodeScanner />
          </IconButton>

          <IconButton onClick={handleThemeToggle} title="Toggle Theme" sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#64b5f6' } }}>
            {theme === 'dark' ? <Brightness7 /> : <Brightness4 />}
          </IconButton>

          <IconButton
            component="a"
            href="https://apps.apple.com/us/app/easy-check-in-check-out-system/id6443909129"
            target="_blank"
            rel="noopener noreferrer"
            title="Get the iOS App"
            sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#64b5f6' } }}
          >
            <Apple />
          </IconButton>
        </Box>

        {/* User Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            color="inherit"
            onClick={handleMenuClick}
            startIcon={
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(100,181,246,0.2)', color: '#64b5f6' }}>
                {user?.firstName?.charAt(0) || <Person />}
              </Avatar>
            }
            sx={{ textTransform: 'none' }}
          >
            {user?.firstName || 'User'}
          </Button>
          
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: { width: 250, mt: 1 }
            }}
          >
            <MenuItem disabled>
              <Box>
                <Typography variant="subtitle2">
                  {user?.firstName} {user?.lastName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.userEmail || 'Guest User'}
                </Typography>
              </Box>
            </MenuItem>
            
            <Divider />
            
            <MenuItem>
              <FormControlLabel
                control={
                  <Switch
                    checked={theme === 'dark'}
                    onChange={handleThemeToggle}
                    size="small"
                  />
                }
                label="Dark Mode"
                sx={{ ml: 0 }}
              />
            </MenuItem>
            
            <MenuItem onClick={() => {
              dispatch(openDialog({ type: 'settings' }));
              handleMenuClose();
            }}>
              <Settings sx={{ mr: 2 }} />
              Settings
            </MenuItem>
            
            <Divider />
            
            <MenuItem onClick={handleSignOut}>
              <Logout sx={{ mr: 2 }} />
              Sign Out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;